// File: src/admin/BlogManagement.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

// --- ICONS ---
const IconEdit = () => <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>;
const IconTrash = () => <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>;
const IconRobot = () => <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path><line x1="12" y1="16" x2="12" y2="16.01"></line><line x1="8" y1="16" x2="8" y2="16.01"></line><line x1="16" y1="16" x2="16" y2="16.01"></line></svg>;

export default function BlogManagement() {
    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [editData, setEditData] = useState(null);
    
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

    const fetchBlogs = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${apiBaseUrl}/api/blogs`);
            const data = await res.json();
            setBlogs(data);
        } catch (error) { console.error(error); } 
        finally { setLoading(false); }
    };

    useEffect(() => { fetchBlogs(); }, []);

    const handleDelete = async (id) => {
        if (!window.confirm("Bạn chắc chắn muốn xóa bài viết này?")) return;
        try {
            const token = localStorage.getItem('skillmatch_token');
            const res = await fetch(`${apiBaseUrl}/api/blogs/${id}`, {
                method: 'DELETE',
                headers: { 'x-auth-token': token }
            });
            if (res.ok) {
                setBlogs(blogs.filter(b => b._id !== id));
                alert("Đã xóa!");
            }
        } catch (e) { alert("Lỗi xóa: " + e.message); }
    };

    const handleGenerateAI = async () => {
        if (!window.confirm("Kích hoạt AI viết 3 bài mới? Việc này mất khoảng 20 giây.")) return;
        setGenerating(true);
        try {
            const res = await fetch(`${apiBaseUrl}/api/blogs/seed-ai`, { method: 'POST' });
            if (res.ok) {
                alert("✅ AI đã viết xong 3 bài mới!");
                fetchBlogs();
            }
        } catch (e) { alert("Lỗi AI: " + e.message); }
        finally { setGenerating(false); }
    };

    const handleUpdate = async () => {
        try {
            const token = localStorage.getItem('skillmatch_token');
            const res = await fetch(`${apiBaseUrl}/api/blogs/${editData._id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'x-auth-token': token },
                body: JSON.stringify(editData)
            });
            if (res.ok) {
                alert("Cập nhật thành công!");
                setEditData(null);
                fetchBlogs();
            }
        } catch (e) { alert("Lỗi cập nhật: " + e.message); }
    };

    return (
        <div className="p-8 bg-gray-50 min-h-screen">
            <div className="max-w-6xl mx-auto">
                <div className="flex justify-between items-center mb-8">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800">Quản lý Blog & Tin tức</h1>
                        <p className="text-gray-500">Tổng số bài viết: {blogs.length}</p>
                    </div>
                    <button 
                        onClick={handleGenerateAI}
                        disabled={generating}
                        className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-5 py-2.5 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all disabled:opacity-70"
                    >
                        {generating ? <span className="animate-spin">⌛</span> : <IconRobot />}
                        {generating ? 'AI Đang viết...' : 'AI Viết bài mới'}
                    </button>
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                                <th className="p-4 text-sm font-bold text-gray-500 uppercase">Hình ảnh</th>
                                <th className="p-4 text-sm font-bold text-gray-500 uppercase">Tiêu đề / Tác giả</th>
                                <th className="p-4 text-sm font-bold text-gray-500 uppercase">Ngành</th>
                                <th className="p-4 text-sm font-bold text-gray-500 uppercase">Thống kê</th>
                                <th className="p-4 text-sm font-bold text-gray-500 uppercase text-right">Hành động</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {blogs.map(blog => (
                                <tr key={blog._id} className="hover:bg-gray-50 transition-colors">
                                    <td className="p-4 w-24">
                                        <img src={blog.thumbnail} alt="" className="w-16 h-12 object-cover rounded-lg bg-gray-200" />
                                    </td>
                                    <td className="p-4">
                                        <Link to={`/blog/${blog.slug}`} className="font-bold text-gray-900 hover:text-blue-600 line-clamp-1 block mb-1">
                                            {blog.title}
                                        </Link>
                                        <div className="flex items-center gap-2 text-xs text-gray-500">
                                            <img src={blog.author?.avatar} className="w-4 h-4 rounded-full" alt="" />
                                            <span>{blog.author?.name}</span>
                                            <span>•</span>
                                            <span>{new Date(blog.publishedAt).toLocaleDateString('vi-VN')}</span>
                                        </div>
                                    </td>
                                    <td className="p-4">
                                        <span className="px-2 py-1 bg-blue-50 text-blue-700 text-xs font-semibold rounded-md">
                                            {blog.industry}
                                        </span>
                                    </td>
                                    <td className="p-4 text-sm text-gray-600">
                                        <div className="flex gap-3">
                                            <span title="Lượt xem">👁️ {blog.views}</span>
                                            <span title="Lượt thích">❤️ {blog.likes?.length || 0}</span>
                                            <span title="Bình luận">💬 {blog.comments?.length || 0}</span>
                                        </div>
                                    </td>
                                    <td className="p-4 text-right">
                                        <div className="flex justify-end gap-2">
                                            <button 
                                                onClick={() => setEditData(blog)}
                                                className="p-2 text-gray-500 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-colors" title="Sửa"
                                            >
                                                <IconEdit />
                                            </button>
                                            <button 
                                                onClick={() => handleDelete(blog._id)}
                                                className="p-2 text-gray-500 hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors" title="Xóa"
                                            >
                                                <IconTrash />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {blogs.length === 0 && !loading && (
                        <div className="p-12 text-center text-gray-400">Chưa có bài viết nào.</div>
                    )}
                </div>
            </div>

            {/* MODAL EDIT - ĐÃ SỬA LỖI MÀU CHỮ */}
            {editData && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl animate-slideUp">
                        <div className="p-6 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white z-10">
                            <h3 className="text-lg font-bold text-gray-800">Chỉnh sửa bài viết</h3>
                            <button onClick={() => setEditData(null)} className="text-gray-400 hover:text-gray-900 text-2xl">&times;</button>
                        </div>
                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1">Tiêu đề</label>
                                <input 
                                    type="text" 
                                    // ✅ ĐÃ THÊM: bg-white text-gray-900
                                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white text-gray-900"
                                    value={editData.title}
                                    onChange={(e) => setEditData({...editData, title: e.target.value})}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1">Tóm tắt (Sapo)</label>
                                <textarea 
                                    // ✅ ĐÃ THÊM: bg-white text-gray-900
                                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white text-gray-900"
                                    rows="3"
                                    value={editData.summary}
                                    onChange={(e) => setEditData({...editData, summary: e.target.value})}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-1">Ngành nghề</label>
                                    <select 
                                        // ✅ ĐÃ THÊM: bg-white text-gray-900
                                        className="w-full p-3 border rounded-lg bg-white text-gray-900"
                                        value={editData.industry}
                                        onChange={(e) => setEditData({...editData, industry: e.target.value})}
                                    >
                                        {['Marketing', 'IT - Phần mềm', 'Tài chính - Kế toán', 'Xây dựng', 'Logistics', 'Nhân sự (HR)', 'Y tế', 'Giáo dục'].map(ind => (
                                            <option key={ind} value={ind}>{ind}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-1">Ảnh bìa (URL)</label>
                                    <input 
                                        type="text" 
                                        // ✅ ĐÃ THÊM: bg-white text-gray-900
                                        className="w-full p-3 border rounded-lg bg-white text-gray-900"
                                        value={editData.thumbnail}
                                        onChange={(e) => setEditData({...editData, thumbnail: e.target.value})}
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1">Nội dung (HTML)</label>
                                <textarea 
                                    // ✅ ĐÃ THÊM: bg-gray-50 text-gray-900 (font code)
                                    className="w-full p-3 border rounded-lg font-mono text-sm bg-gray-50 text-gray-900 focus:ring-2 focus:ring-blue-500 outline-none"
                                    rows="10"
                                    value={editData.content}
                                    onChange={(e) => setEditData({...editData, content: e.target.value})}
                                />
                                <p className="text-xs text-gray-400 mt-1">Hỗ trợ các thẻ: &lt;h2&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;strong&gt;</p>
                            </div>
                        </div>
                        <div className="p-6 border-t border-gray-100 flex justify-end gap-3 bg-gray-50 sticky bottom-0">
                            <button onClick={() => setEditData(null)} className="px-5 py-2.5 rounded-lg border bg-white text-gray-700 font-semibold hover:bg-gray-100">Hủy</button>
                            <button onClick={handleUpdate} className="px-5 py-2.5 rounded-lg bg-blue-600 text-white font-bold hover:bg-blue-700">Lưu thay đổi</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}